import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  EXPORT_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import ApprovalStatusesModal from "../../modal/ApprovalStatusesModal";
import {
  downloadFile,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import { ExportExternalUsersReportForExcelPDFBody } from "../../../hooks/report/interface";
import {
  useExportExternalUsersReportForExcel,
  useExportExternalUsersReportForPDF,
} from "../../../hooks/report/mutation";
import StatusCodes from "../../../constants/StatusCodes";

interface ExternalUser {
  s_FullName: string;
  s_CompanyName: string;
  s_Email: string;
  s_MobileNumber: string;
  s_UserName: string;
  dt_CreatedDate: string;
  exportBody: ExportExternalUsersReportForExcelPDFBody;
}

interface ExternalUsersReportTableProps {
  allExternalUsers: ExternalUser[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  exportBody?: ExportExternalUsersReportForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const ExternalUsersReportTable = ({
  allExternalUsers,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: ExternalUsersReportTableProps) => {
  const { t, i18n } = useTranslation();

  const [isApprovalStatusesModalOpen, setIsApprovalStatusesModalOpen] =
    useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const { mutateAsync: ExportExternalUsersReportForExcel } =
    useExportExternalUsersReportForExcel();

  const { mutateAsync: ExportExternalUsersReportForPDF } =
    useExportExternalUsersReportForPDF();

  const columns: ColumnDef<ExternalUser>[] = useMemo(
    () => [
      {
        accessorKey: "s_FullName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("fullName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_FullName}</span>;
        },
      },
      {
        accessorKey: "s_CompanyName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("companyName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_CompanyName}</span>;
        },
      },
      {
        accessorKey: "s_Email",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("email")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Email}</span>;
        },
      },
      {
        accessorKey: "s_MobileNumber",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("mobileNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_MobileNumber}</span>;
        },
      },
      {
        accessorKey: "dt_CreatedDate",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("registrationDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {formattedDateTimeWithoutSec(row.original.dt_CreatedDate)}
            </span>
          );
        },
      },
    ],
    [t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportExternalUsersReportForExcel(body);
        } else {
          res = await ExportExternalUsersReportForPDF(body);
        }

        downloadFile(res, REPORT_NAMES.EXTERNAL_USERS_REPORT);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportExternalUsersReportForExcel,
      ExportExternalUsersReportForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allExternalUsers}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isExportExcel
        onExport={onExport}
        isSearch
        tableId={TABLE_IDS.EXTERNAL_USERS_REPORT}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <ApprovalStatusesModal
        isOpen={isApprovalStatusesModalOpen}
        onOpenChange={() => setIsApprovalStatusesModalOpen(false)}
      />
    </>
  );
};

export default memo(ExternalUsersReportTable);
